import Vue from 'vue';

import entity from '@/../region.json';
import VueRouter from 'vue-router';

import checkForCityOfLondon from '../../utils/redirectHelpers';
import setPageTitle from '../../utils/setPageTitle';
import i18n, { initTranslations } from '../i18n';
import matchEntities, {
  OnlyRunIfNetherlandsEntity,
  specialCaseSpain,
} from '../shared/entityHelpers';
import featureFlags from '../../utils/featureFlags';
import cityNames from '../data/netherlandsCities';

const Home = () =>
  import(
    /* webpackChunkName: "home" */

    '../pages/HomePage.vue'
  );
const Experience = () =>
  import(
    /* webpackChunkName: "experience" */

    '../pages/ExperiencePage.vue'
  );
const Privacy = () =>
  import(
    /* webpackChunkName: "privacy" */

    '../pages/PrivacyPage.vue'
  );
const Agreement = () =>
  import(
    /* webpackChunkName: "agreement" */

    '../pages/AgreementPage.vue'
  );
const Manual = () =>
  import(
    /* webpackChunkName: "manual" */

    '../pages/ManualPage.vue'
  );
const Invoice = () =>
  import(
    /* webpackChunkName: "invoice" */

    '../pages/InvoicePage.vue'
  );
const Impressum = () =>
  import(
    /* webpackChunkName: "impressum" */

    '../pages/ImpressumPage.vue'
  );
const Meter = () =>
  import(
    /* webpackChunkName: "tech" */

    '../pages/MeterPage.vue'
  );
const TechLogin = () =>
  import(
    /* webpackChunkName: "login" */

    '../pages/TechPages/Login.vue'
  );
const TechResetPassword = () =>
  import(
    /* webpackChunkName: "reset" */

    '../pages/TechPages/ResetPassword.vue'
  );
const TechConfirm = () =>
  import(
    /* webpackChunkName: "confirm" */

    '../pages/TechPages/Confirm.vue'
  );
const ErrorPage = () =>
  import(
    /* webpackChunkName: "error" */

    '../pages/TechPages/Error.vue'
  );
const NotFound = () =>
  import(
    /* webpackChunkName: "notfound" */

    '../pages/NotFound.vue'
  );
const FAQPage = () =>
  import(
    /* webpackChunkName: "faq" */

    '../pages/FAQPage.vue'
  );
const Register = () =>
  import(
    /* webpackChunkName: "register" */

    '../pages/Register.vue'
  );
const ContactForm = () =>
  import(
    /* webpackChunkName: "contact" */

    '../pages/Contact.vue'
  );
const ChangeCountry = () =>
  import(
    /* webpackChunkName: "change-country" */

    '../pages/ChangeCountry.vue'
  );
const ChargeHelp = () =>
  import(
    /* webpackChunkName: "chargehelp" */

    '../pages/ChargeHelp.vue'
  );

const NetherlandsProcessDescription = () =>
  import(
    /* webpackChunkName: "NetherlandsProcessDescription" */

    '../pages/netherlands/ProcessDescription.vue'
  );
const Unsubscribe = () =>
  import(
    /* webpackChunkName: "unsubscribe" */

    '../pages/Unsubscribe.vue'
  );
const OpenMarketCities = () =>
  import(
    /* webpackChunkName: "OpenMarketCities" */

    '../pages/netherlands/OpenMarketCities.vue'
  );
const AccountDeleted = () =>
  import(
    /* webpackChunkName: "accountDeleted" */

    '../pages/AccountDeleted.vue'
  );

const AccountDeletionRequest = () =>
  import(
    /* webpackChunkName: "AccountDeletionRequest" */

    '../pages/RequestAccountDeletion.vue'
  );

Vue.use(VueRouter);

const router = new VueRouter({
  mode: 'history',
  linkActiveClass: 'router-link-active',
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: '',
      redirect: `/${entity.defaultLang}`,
    },
    {
      path: '/appdownload',
      meta: {
        navless: true,
        footerless: true,
      },
      beforeEnter: () => {
        if (navigator.userAgent && navigator.userAgent.includes('Android')) {
          window.location =
            'http://play.google.com/store/apps/details?id=eu.qwello.app';
          return true;
        }
        window.location =
          'https://apps.apple.com/us/app/qwello/id1499322729?ls=1';
        return true;
      },
    },
    {
      path: '/app/:lang/login/:userId/:token',
      meta: {
        navless: true,
        footerless: true,
      },
      beforeEnter: (to, from, next) => {
        if (to.query.market) {
          if (navigator.userAgent && navigator.userAgent.includes('Android')) {
            window.location =
              'http://play.google.com/store/apps/details?id=eu.qwello.app';
            return true;
          }
          window.location =
            'https://apps.apple.com/us/app/qwello/id1499322729?ls=1';
          return true;
        }
        return next(
          `${to.params.lang}/login/${to.params.userId}/${to.params.token}`
        );
      },
    },
    {
      path: '/app/:lang/login/:userId/:token',
      redirect: ':lang/login/:userId/:token',
      meta: {
        navless: true,
        footerless: true,
      },
    },
    {
      path: '/meter/:id',
      redirect: `${entity.defaultLang}/meter/:id`,
    },
    {
      path: '/app/:lang/delete/:userUuid/:token',
      redirect: ':lang/delete/:userUuid/:token',
    },
    {
      path: '/app/:lang/reset-password/:userId/:token',
      redirect: ':lang/reset-password/:userId/:token',
      meta: {
        navless: true,
        footerless: true,
      },
    },
    {
      path: '/:lang/unsubscribe/:uuid',
      component: Unsubscribe,
      meta: {
        footerless: true,
      },
    },
    {
      path: '/app/:lang/confirm/:userId/:token',
      redirect: ':lang/confirm/:userId/:token',
      meta: {
        navless: true,
        footerless: true,
      },
    },
    {
      path: '/app/:lang/registration-confirmed/:userId/:token',
      meta: {
        navless: true,
        footerless: true,
      },
      beforeEnter: () => {
        if (navigator.userAgent && navigator.userAgent.includes('Android')) {
          window.location =
            'http://play.google.com/store/apps/details?id=eu.qwello.app';
          return true;
        }
        window.location =
          'https://apps.apple.com/us/app/qwello/id1499322729?ls=1';
        return true;
      },
    },
    {
      path: '/:lang',
      component: {
        render: h => h('router-view'),
      },
      beforeEnter: async (to, from, next) => {
        const langArr = to.params.lang.split('/');
        const lang = langArr[0];

        const shouldRedirectToPopup = checkForCityOfLondon(entity, to);

        if (shouldRedirectToPopup) {
          next('/en/?popup=show');
          return;
        }
        // handle login/registration/password-reset
        if (
          ['Login', 'Confirm', 'tech-error', 'Reset', 'Meter'].includes(to.name)
        ) {
          next();
        } else if (
          specialCaseSpain(entity.entity, lang) ||
          !Object.keys(entity.supportedLanguages).includes(lang)
        ) {
          // load English translations
          await initTranslations('en');
          // remove wrong language from path
          const redirectedPath = to.path.replace(`${lang}/`, '');
          // redirect unnknown language option to EN
          next(`en${redirectedPath}`);
          // set new language
          i18n.locale = 'en';
        } else {
          next();
        }
      },
      children: [
        {
          path: 'transparency',
          beforeEnter(to) {
            const germanURL =
              'https://www.safe-ev.de/de/transparenzsoftware.php';
            const englishUrl =
              'https://www.safe-ev.de/en/transparency_software.php';
            let url = germanURL;

            const langArr = to.params.lang.split('/');

            if (langArr[0] !== 'de') {
              url = englishUrl;
            }
            window.location = url;
          },
        },
        {
          path: '',
          name: 'Home',
          component: Home,
          meta: { navLight: true },
        },
        {
          meta: { navLight: true },
          path: 'laadpaal-aanvragen-:name',
          async beforeEnter(to, _from, next) {
            const featureNotEnabled = !featureFlags.enablePublicCitiesPages();

            const wrongCity = !cityNames.includes(
              to.params.name?.toLocaleLowerCase()
            );
            if (featureNotEnabled || wrongCity) {
              return next('/404');
            }
            if (to.params.lang !== 'nl') {
              await initTranslations('nl');
              return next('/nl/laadpaal-aanvragen-zaltbommel');
            }
            return next();
          },
          name: 'OpenMarketCities',
          component: OpenMarketCities,
        },
        {
          path: 'cityoflondon',
          redirect: '/?popup=show',
        },
        {
          path: 'privacy',
          name: 'Privacy',
          component: Privacy,
        },
        {
          path: 'terms',
          name: 'User Agreement',
          component: Agreement,
        },
        {
          path: 'manual',
          name: 'User Manual',
          component: Manual,
        },
        {
          path: 'invoice',
          name: 'Invoice',
          component: Invoice,
        },
        {
          path: 'receipt',
          name: 'Receipt',
          component: Invoice,
        },
        {
          path: 'impressum',
          name: 'Impressum',
          component: Impressum,
        },
        {
          path: 'faq',
          name: 'FAQ',
          component: FAQPage,
        },
        {
          path: 'experience',
          name: 'Experience',
          component: Experience,
        },
        {
          path: 'contact',
          name: 'Contact',
          component: ContactForm,
        },
        {
          path: 'register',
          name: 'Register',
          component: Register,
        },
        {
          path: 'careers',
          name: 'Careers',
          beforeEnter: () => {
            window.location = 'https://qwello.eu/en/careers';
          },
        },
        { path: 'meter/:id', component: Meter, name: 'Meter' },
        {
          path: 'login/:userId/:token',
          component: TechLogin,
          name: 'Login',
          meta: { navless: true },
        },
        {
          path: 'reset-password/:userId/:token',
          component: TechResetPassword,
          name: 'Reset',
          meta: { navless: true },
        },
        {
          path: 'confirm/:userId/:token',
          component: TechConfirm,
          name: 'Confirm',
          meta: { navless: true },
        },
        {
          path: 'error',
          component: ErrorPage,
          name: 'Error',
          meta: { navless: true },
        },
        {
          path: 'change-country',
          component: ChangeCountry,
          name: 'Change Country',
        },
        {
          path: 'how-to',
          component: ChargeHelp,
          name: 'How to Charge',
        },
        {
          path: 'procesbeschrijving',
          component: NetherlandsProcessDescription,
          name: 'Procesbeschrijving',
          beforeEnter(_to, _from, next) {
            if (!matchEntities(['NETHERLANDS'])) {
              next('/');
              return;
            }
            next();
          },
        },
        {
          path: 'delete/:userUuid/:token',
          name: 'Account Deleted',
          component: AccountDeleted,
        },
        {
          path: 'delete-account',
          name: 'Delete Account',
          component: AccountDeletionRequest,
        },
        {
          path: '*',
          name: 'Not Found',
          component: NotFound,
          meta: { footerFixed: true },
        },
      ],
    },
  ],
});
let wasNavless = false;
// When a page is _navless_ Vue router is still loading in the nav-bar
// whilst waiting for the page to be asynchronously fetched. This
// means the nav-bar appears momentarily for pages that don't need it.
// Unfortunately, Vue router doesn't have a nice way of avoiding this problem
// TODO(greg) find a more elegant/less-hacky way to preventing this navigation flash
const isNavlessPage = text =>
  ['Login', 'Confirm', 'tech-error', 'Reset'].includes(text);

// this runs before the page has loaded.
router.beforeEach((to, _from, next) => {
  setPageTitle(to);
  // if the page is navless then ensure navbar does not load whilst fetching page
  if (isNavlessPage(to.name)) {
    const { body } = document;
    if (body) {
      // stop nav-bar flash from appearing
      body.style = '--nav-display: none';
      wasNavless = true;
    }
  }
  // allow these pages to be accessed by qwelo.de/es (spanish users)
  if (['Login', 'Confirm', 'tech-error', 'Reset', 'Meter'].includes(to.name)) {
    next();
    return;
  }
  if (OnlyRunIfNetherlandsEntity(to, next)) {
    return;
  }
  // If the lang = 'es' and the website is 'qwello.de' we
  // redirect them to the default entity's language

  if (specialCaseSpain(entity.entity, to.params.lang)) {
    next({ path: to.path.replace('es', entity.defaultLang) });
    return;
  }

  // otherwise continue
  next();
});

router.afterEach(() => {
  // Now the page has loaded, the styling must be reset.
  // Only do this if the page was previously _navless_.
  if (wasNavless) {
    document.body.style = '--nav-display: flex';
    wasNavless = false;
  }
});
export default router;
